import { ProductTexture, DesignItem } from 'CommonData/poco';
import { ContextBox } from 'CommonScripts/contextBox';
import { LayingPatternViewer } from 'CommonScripts/layingPatternViewer';
import { LoadingHelper } from 'CommonScripts/loadingHelper';
import { SceneTime } from 'CommonScripts/sceneTime';
import { Clone } from 'CommonScripts/clone';
import { NavHelper } from './nav';
import { baseProductTextures } from './textureSelect';
import { CatalogueContext } from 'CommonScripts/catalogueContext';
import { DomesticBasket } from './domesticBasket';

const saveDesignButtonEl: HTMLButtonElement = document.getElementsByClassName('js-favourite-save-design')[0] as HTMLButtonElement;
const designItemEls: HTMLCollection = document.getElementsByClassName('js-favourite-design-item');
const originalBaseFavouriteDesignGroupEl: Element = document.getElementsByClassName('js-favourite-design-group')[0];
const baseFavouriteDesignGroupEl: HTMLElement = originalBaseFavouriteDesignGroupEl.cloneNode(true) as HTMLElement;
originalBaseFavouriteDesignGroupEl.remove();

export class FavouriteDesigns {
    private static readonly STORAGE_KEY: string = 'vis_favourite_designs';

    public static async onDesignClick(dataEl: Element) {
        if (!dataEl) {
            console.error('FavouriteDesigns.onDesignClick: Invalid dataEl')
            return;
        }

        try {
            this.deselectAllDesigns();
            dataEl.classList.add('selected');

            LoadingHelper.start();

            const modelUrl: string = dataEl.getAttribute('data-model-url');
            const patternId: number = parseInt(dataEl.getAttribute('data-pattern-id'));
            const sceneMinuteAsString: string = dataEl.getAttribute('data-minute');
            const jointKey: string = dataEl.getAttribute('data-joint-key');
            const baseTextureKey: string = dataEl.getAttribute('data-texture');

            let matchingProductTexture: ProductTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === baseTextureKey));
            if (!matchingProductTexture) {
                const textureKey: string = baseTextureKey.split('|')[0];
                matchingProductTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === textureKey));
            }

            matchingProductTexture.textureKey = baseTextureKey;

            await LayingPatternViewer.updateTexture({ productTexture: matchingProductTexture, layingPatternId: patternId, jointingKey: jointKey });
            
            if (modelUrl) {
                await LayingPatternViewer.updateBaseModel(modelUrl);
            }

            if (sceneMinuteAsString) {
                SceneTime.setTimeByMinute(parseInt(sceneMinuteAsString));
            }

            LayingPatternViewer.updateUrl();
        } finally {
            NavHelper.closeLeftNav();
            NavHelper.isPatternContext = false;
            LoadingHelper.stop();
            
            if (CatalogueContext.isDomestic() || CatalogueContext.isPOS()) {
                DomesticBasket.render();
            }
        }
    }

    public static saveDesign() {
        const items: Array<DesignItem> = this.getDesignItems();
        const currentTextureObject: any = LayingPatternViewer.getCurrentTexture();
        const currentModelUrl: string = LayingPatternViewer.getCurrentModelUrl();

        let matchingProductTexture: ProductTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === currentTextureObject.ProductTextureKey));
        if (!matchingProductTexture) {
            matchingProductTexture = Clone.deep(baseProductTextures.find((pt: ProductTexture) => pt.textureKey === currentTextureObject.ProductTextureKey.split('|')[0]));
        }

        const snapshotImageUrl: string = 
            `https://media.marshalls.co.uk/image/upload/w_100,h_100,c_fill/VisualiserMaterials/Textures_Jointing/${currentTextureObject.ProductTextureKey}/${currentTextureObject.JointWidthMM}_${currentTextureObject.JointTextureKey}_${currentTextureObject.MaskId}_${currentTextureObject.PatternId}_diffuse_med.jpg`

        // save to the api when it is implemented in the future. should be a quick replace job as long as we have a common DesignItem poco (to do)
        items.push(new DesignItem ({
            name: `${matchingProductTexture.name}. Ptn:${currentTextureObject.PatternId}`,
            textureKey: currentTextureObject.ProductTextureKey,
            jointKey: currentTextureObject.JointTextureKey,
            patternId: currentTextureObject.PatternId,
            modelUrl: currentModelUrl,
            environmentTimeInMinutes: SceneTime.getTimeInMinutes(),
            timestamp: new Date(),
            snapshotImageUrl
        }));

        this.saveDesignItems(items);
    }

    public static render(isInitialRender: boolean = false) {
        const designGroupEls: Array<Element> = [].slice.call(document.getElementsByClassName('js-favourite-design-group'));
        designGroupEls.forEach((el: Element) => el.remove());

        const items: Array<DesignItem> = this.getDesignItems();

        const favouriteCountEl: Element = document.getElementsByClassName('js-favourite-count')[0]
        if (favouriteCountEl) {
            favouriteCountEl.innerHTML = items.length.toString();
        }
        
        const groupedItems: Array<any> = [];
        items.forEach((item: DesignItem) => {
            if (!item) {
                return;
            }

            const date: string = item.timestamp.toString().split('T')[0];
            const matchingGroupedItem: any = groupedItems.find((gi: any) => gi.date.toString().split('T')[0] === date);

            if (matchingGroupedItem) {
                matchingGroupedItem.designItems.push(item);
            } else {
                groupedItems.push({
                    date: item.timestamp,
                    designItems: [item],
                });
            }
        });

        groupedItems.forEach((gi: any) => {
            const baseGroupEl: HTMLElement = baseFavouriteDesignGroupEl.cloneNode(true) as HTMLElement;
            const groupDateEl: Element = baseGroupEl.getElementsByClassName('js-favourite-design-group-date')[0];
            const designListEl: Element = baseGroupEl.getElementsByClassName('js-favourite-design-list')[0];
            const baseDesignItemEl: HTMLElement = designListEl.getElementsByClassName('js-favourite-design-item')[0].cloneNode(true) as HTMLElement;

            designListEl.innerHTML = '';
            groupDateEl.innerHTML = new Date(gi.date).toLocaleDateString('en-GB');

            gi.designItems.forEach((di: DesignItem) => {
                const designItemEl: HTMLElement = baseDesignItemEl.cloneNode(true) as HTMLElement;
                const designItemSnapshotEl: HTMLImageElement = designItemEl.getElementsByClassName('js-favourite-design-item-snapshot')[0] as HTMLImageElement;
                const designItemNameEl: Element = designItemEl.getElementsByClassName('js-favourite-design-item-name')[0];
                const designItemTimeEl: Element = designItemEl.getElementsByClassName('js-favourite-design-item-time')[0];

                designItemEl.setAttribute('data-texture', di.textureKey);
                designItemEl.setAttribute('data-pattern-id', di.patternId.toString());
                designItemEl.setAttribute('data-joint-key', di.jointKey);
                designItemEl.setAttribute('data-model-url', di.modelUrl);
                designItemEl.setAttribute('data-minute', di.environmentTimeInMinutes.toString());
                designItemEl.setAttribute('data-id', di.id);

                designItemSnapshotEl.setAttribute('src', di.snapshotImageUrl);
                designItemNameEl.innerHTML = di.name;
                designItemTimeEl.innerHTML = new Date(di.timestamp).toLocaleTimeString();

                designListEl.appendChild(designItemEl);
            });

            baseGroupEl.appendChild(designListEl);

            document.getElementsByClassName('js-favourite-content')[0].insertBefore(baseGroupEl, saveDesignButtonEl);
        });

        if (items && items.length >= 4) {
            saveDesignButtonEl.classList.add('disabled');
        } else {
            saveDesignButtonEl.classList.remove('disabled');
        }

        if (isInitialRender) {
            this.setEventListeners()
        } else {
            this.setDesignItemEvents();
        }
    }

    public static onOptionsMenuClick(menuIconEl: Element) {
        const boxEl: Element = menuIconEl.parentElement.getElementsByClassName('js-design-options-box')[0];
        ContextBox.open(boxEl, menuIconEl);
    }

    public static deselectAllDesigns() {
        [].slice.call(designItemEls).forEach((el: Element) => { 
            el.classList.remove('selected');
        });
    }

    private static saveDesignItems(items: Array<DesignItem>) {
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
        // localStorage.setItem(this.STORAGE_KEY, null);

        this.render();
    }

    private static getDesignItems() {
        // this data will be returned by the api in the future so keep functionality common between local storage and api data
        const data: Array<DesignItem> = JSON.parse(localStorage.getItem(this.STORAGE_KEY));
        return data || [];
    }

    private static setDesignItemEvents() {
        [].slice.call(designItemEls).forEach((el: Element) => { 
            el.addEventListener('click', () => {
                this.onDesignClick(el);
            });

            const designOptionsIconEl: Element = el.getElementsByClassName('js-design-options-icon')[0];
            if (designOptionsIconEl) {
                const downloadPDFEl: Element = el.getElementsByClassName('js-design-options-download-pdf')[0];
                if (downloadPDFEl) {
                    downloadPDFEl.addEventListener('click', (e: Event) => {
                        e.stopPropagation();
                        window.open('https://media.marshalls.co.uk/image/upload/MoodboardTemplate.pdf');
                    });
                }

                const deleteDesignItemEl: Element = el.getElementsByClassName('js-design-option-delete')[0];
                if (deleteDesignItemEl) {
                    deleteDesignItemEl.addEventListener('click', (e: Event) => {
                        e.stopPropagation();
                        const designItemId: string = el.getAttribute('data-id');

                        let items: Array<DesignItem> = this.getDesignItems();
                        items = items.filter((di: DesignItem) => di.id !== designItemId);

                        const boxEls: HTMLCollectionOf<Element> = document.getElementsByClassName('js-design-options-box');
                        const openBoxEl: Element = boxEls[boxEls.length - 1];
                        ContextBox.close(openBoxEl, designOptionsIconEl);

                        this.saveDesignItems(items);
                    });
                }
                
                designOptionsIconEl.addEventListener('click', (e: Event) => {
                    e.stopPropagation();
                    this.onOptionsMenuClick(designOptionsIconEl);
                });

                // add other events
            }
        });
    }

    private static setEventListeners() {
        if (saveDesignButtonEl) {
            saveDesignButtonEl.addEventListener('click', () => {
                if (!saveDesignButtonEl.classList.contains('disabled')) {
                    this.saveDesign();
                }
            });
        }

        this.setDesignItemEvents();
    }
}

FavouriteDesigns.render(true);